import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"; // To access teacherId from the URL
import {
  fetchTeacherInformation,
  updateAdminTeacher,
  uploadTeacherProfilePicture,
} from "../../../redux/Actions/teacherAdminActions";
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AdminNavbar from "./AdminNavbar"; // Assuming AdminNavbar component is already implemented

const TeacherInformation = () => {
  const { teacherId } = useParams(); // Fetch the teacherId from the route parameter
  const dispatch = useDispatch();

  // States for controlling table collapse
  const [pendingOpen, setPendingOpen] = useState(true);
  const [approvedOpen, setApprovedOpen] = useState(true);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dateOfBirth: "",
    job: "",
    nationality: "",
    accommodation: "",
    phoneNumber: "",
    displayOrder: "",
    bio: "",
    bioAr: "",
    bioDe: "",
    study: "",
    studyAr: "",
    studyDe: "",
    experience: "",
    experienceAr: "",
    experienceDe: "",
    specialization: "",
    specializationAr: "",
    specializationDe: "",
    academicQualifications: "",
    academicQualificationsAr: "",
    academicQualificationsDe: "",
    courseDescription: "",
    courseDescriptionAr: "",
    courseDescriptionDe: "",
    confirmedFromAdmin: false,
    status: "pending",
  });

  // Fetch teacher details and courses on component mount
  useEffect(() => {
    if (teacherId) {
      dispatch(fetchTeacherInformation(teacherId));
    }
  }, [dispatch, teacherId]);

  // Get teacher details and loading/error status from the Redux store
  const { teacherDetails, loading, saving, uploadingPicture, error, saveError } = useSelector(
    (state) => state.teacherDetails
  );

  useEffect(() => {
    if (!teacherDetails) {
      return;
    }

    setProfileForm({
      firstName: teacherDetails.firstName || "",
      lastName: teacherDetails.lastName || "",
      email: teacherDetails.email || "",
      password: "",
      dateOfBirth: teacherDetails.dateOfBirth
        ? new Date(teacherDetails.dateOfBirth).toISOString().split("T")[0]
        : "",
      job: teacherDetails.job || "",
      nationality: teacherDetails.nationality || "",
      accommodation: teacherDetails.accommodation || "",
      phoneNumber: teacherDetails.phoneNumber || "",
      displayOrder: teacherDetails.displayOrder ?? "",
      bio: teacherDetails.bio || "",
      bioAr: teacherDetails.bioAr || "",
      bioDe: teacherDetails.bioDe || "",
      study: teacherDetails.study || "",
      studyAr: teacherDetails.studyAr || "",
      studyDe: teacherDetails.studyDe || "",
      experience: teacherDetails.experience || "",
      experienceAr: teacherDetails.experienceAr || "",
      experienceDe: teacherDetails.experienceDe || "",
      specialization: teacherDetails.specialization || "",
      specializationAr: teacherDetails.specializationAr || "",
      specializationDe: teacherDetails.specializationDe || "",
      academicQualifications: teacherDetails.academicQualifications || "",
      academicQualificationsAr: teacherDetails.academicQualificationsAr || "",
      academicQualificationsDe: teacherDetails.academicQualificationsDe || "",
      courseDescription: teacherDetails.courseDescription || "",
      courseDescriptionAr: teacherDetails.courseDescriptionAr || "",
      courseDescriptionDe: teacherDetails.courseDescriptionDe || "",
      confirmedFromAdmin: Boolean(teacherDetails.confirmedFromAdmin),
      status: teacherDetails.status || "pending",
    });
  }, [teacherDetails]);

  if (loading) {
    return <CircularProgress />; // Show a loader when data is being fetched
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>; // Show error message
  }

  if (!teacherDetails) {
    return null; // In case no teacherDetails are available
  }

  // Destructure the teacher information and courses
  const {
    firstName,
    lastName,
    email,
    dateOfBirth,
    job,
    nationality,
    accommodation,
    phoneNumber,
    roleName,
    qualifications,
    study,
    experience,
    specialization,
    academicQualifications,
    courseDescription,
    pendingCourses,
    approvedCourses,
    profilePicture,
  } = teacherDetails;

  const handleProfileFieldChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleProfileSave = () => {
    const payload = {
      ...profileForm,
      dateOfBirth: profileForm.dateOfBirth || null,
      password: profileForm.password || null,
      displayOrder: profileForm.displayOrder === "" ? null : Number(profileForm.displayOrder),
    };
    dispatch(updateAdminTeacher(teacherId, payload));
  };

  const handlePictureUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    dispatch(uploadTeacherProfilePicture(teacherId, file));
    event.target.value = "";
  };

  const getImageSrc = (value) => {
    if (!value) return "default-teacher-image.jpg";
    if (String(value).startsWith("/api/")) {
      return `${process.env.REACT_APP_API_URL}${value}`;
    }
    return value;
  };

  return (
    <div>
      <AdminNavbar /> {/* Include the admin navbar for navigation */}
      <Container>
        <Box sx={{ my: 4, display: "flex", alignItems: "flex-start" }}>
          {/* Teacher details */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              Teacher Information: {firstName} {lastName}
            </Typography>

            <Box mb={4}>
              <Typography variant="h6">Teacher Details:</Typography>
              <Typography>
                <strong>Email:</strong> {email}
              </Typography>
              <Typography>
                <strong>Date of Birth:</strong>{" "}
                {dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : "-"}
              </Typography>
              <Typography>
                <strong>Job:</strong> {job}
              </Typography>
              <Typography>
                <strong>Nationality:</strong> {nationality}
              </Typography>
              <Typography>
                <strong>Accommodation:</strong> {accommodation}
              </Typography>
              <Typography>
                <strong>Phone Number:</strong> {phoneNumber}
              </Typography>
              <Typography>
                <strong>Role:</strong> {roleName}
              </Typography>
              <Typography>
                <strong>Qualifications:</strong>{" "}
                {qualifications?.join(", ") || "None"}
              </Typography>
            </Box>

            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Public Profile Preview:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  mb: 3,
                }}
              >
                {[
                  {
                    title: "Studium",
                    value: study || "-",
                  },
                  {
                    title: "Erfahrung",
                    value: experience || "-",
                  },
                  {
                    title: "Spezialisierung",
                    value: specialization || "-",
                  },
                ].map((item) => (
                  <Paper
                    key={item.title}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Typography fontWeight={700}>{item.title}</Typography>
                    <Typography variant="body2">{item.value}</Typography>
                  </Paper>
                ))}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Akademische Qualifikationen
                  </Typography>
                  <Typography whiteSpace="pre-line">
                    {academicQualifications || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Meine Kurse
                  </Typography>
                  <Typography whiteSpace="pre-line">
                    {courseDescription || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                Edit Public Teacher Profile:
              </Typography>
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {saveError}
                </Alert>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Grid container spacing={2}>
                  {[
                    ["firstName", "First Name"],
                    ["lastName", "Last Name"],
                    ["email", "Email"],
                    ["password", "New Password"],
                    ["dateOfBirth", "Date of Birth"],
                    ["job", "Job"],
                    ["nationality", "Nationality"],
                    ["accommodation", "Accommodation"],
                    ["phoneNumber", "Phone Number"],
                    ["displayOrder", "Team Order"],
                  ].map(([name, label]) => (
                    <Grid item xs={12} sm={6} md={4} key={name}>
                      <TextField
                        label={label}
                        name={name}
                        value={profileForm[name]}
                        onChange={handleProfileFieldChange}
                        fullWidth
                        type={
                          name === "password"
                            ? "password"
                            : name === "dateOfBirth"
                            ? "date"
                            : name === "displayOrder"
                            ? "number"
                            : "text"
                        }
                        InputLabelProps={name === "dateOfBirth" ? { shrink: true } : undefined}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Status"
                      name="status"
                      value={profileForm.status}
                      onChange={handleProfileFieldChange}
                      fullWidth
                    >
                      <MenuItem value="pending">pending</MenuItem>
                      <MenuItem value="approved">approved</MenuItem>
                      <MenuItem value="rejected">rejected</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="confirmedFromAdmin"
                          checked={profileForm.confirmedFromAdmin}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              confirmedFromAdmin: event.target.checked,
                              status: event.target.checked ? "approved" : current.status,
                            }))
                          }
                        />
                      }
                      label="Confirmed from admin"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Bio"
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileFieldChange}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Bio (AR)"
                      name="bioAr"
                      value={profileForm.bioAr}
                      onChange={handleProfileFieldChange}
                      fullWidth
                      multiline
                      minRows={3}
                      inputProps={{ dir: "rtl" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Bio (DE)"
                      name="bioDe"
                      value={profileForm.bioDe}
                      onChange={handleProfileFieldChange}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Studium (EN/default)"
                  name="study"
                  value={profileForm.study}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Erfahrung (EN/default)"
                  name="experience"
                  value={profileForm.experience}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Spezialisierung (EN/default)"
                  name="specialization"
                  value={profileForm.specialization}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Studium (AR)"
                  name="studyAr"
                  value={profileForm.studyAr}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  inputProps={{ dir: "rtl" }}
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Erfahrung (AR)"
                  name="experienceAr"
                  value={profileForm.experienceAr}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  inputProps={{ dir: "rtl" }}
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Spezialisierung (AR)"
                  name="specializationAr"
                  value={profileForm.specializationAr}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  inputProps={{ dir: "rtl" }}
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Studium (DE)"
                  name="studyDe"
                  value={profileForm.studyDe}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Erfahrung (DE)"
                  name="experienceDe"
                  value={profileForm.experienceDe}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                <TextField
                  label="Spezialisierung (DE)"
                  name="specializationDe"
                  value={profileForm.specializationDe}
                  onChange={handleProfileFieldChange}
                  fullWidth
                />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                <TextField
                  label="Academic qualifications (EN/default)"
                  name="academicQualifications"
                  value={profileForm.academicQualifications}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                />
                <TextField
                  label="My courses (EN/default)"
                  name="courseDescription"
                  value={profileForm.courseDescription}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                />
                <TextField
                  label="Academic qualifications (AR)"
                  name="academicQualificationsAr"
                  value={profileForm.academicQualificationsAr}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                  inputProps={{ dir: "rtl" }}
                />
                <TextField
                  label="My courses (AR)"
                  name="courseDescriptionAr"
                  value={profileForm.courseDescriptionAr}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                  inputProps={{ dir: "rtl" }}
                />
                <TextField
                  label="Academic qualifications (DE)"
                  name="academicQualificationsDe"
                  value={profileForm.academicQualificationsDe}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                />
                <TextField
                  label="My courses (DE)"
                  name="courseDescriptionDe"
                  value={profileForm.courseDescriptionDe}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  multiline
                  minRows={5}
                />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleProfileSave}
                disabled={saving}
                sx={{ mt: 2 }}
              >
                {saving ? "Saving..." : "Save Teacher"}
              </Button>
            </Box>
          </Box>

          {/* Teacher image */}
          <Box sx={{ flex: 1, textAlign: "center", ml: 4 }}>
            <Typography variant="h6">Teacher Image:</Typography>
            <img
              src={getImageSrc(profilePicture)} // Add default image if not available
              alt={`${firstName} ${lastName}`}
              style={{
                width: "200px",
                height: "200px",
                objectFit: "cover",
                borderRadius: "8px",
              }} // Squared image
            />
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                component="label"
                disabled={uploadingPicture}
                sx={{ textTransform: "none" }}
              >
                {uploadingPicture ? "Uploading..." : "Upload Picture"}
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handlePictureUpload}
                />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Pending Courses (Collapsible) */}
        <Box mt={4}>
          <Box
            onClick={() => setPendingOpen(!pendingOpen)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: pendingOpen ? "#007bff" : "#6c757d", // Bootstrap-style colors for classic button look
              color: "#fff",
              padding: "8px 16px",
              borderRadius: "4px",
              transition: "background-color 0.3s",
              "&:hover": {
                backgroundColor: pendingOpen ? "#0056b3" : "#5a6268",
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              {pendingOpen ? "Hide Pending Courses" : "Show Pending Courses"}
            </Typography>
          </Box>
          <Collapse in={pendingOpen}>
            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              sx={{ textAlign: "center" }}
            >
              Pending Courses
            </Typography>
            {pendingCourses && pendingCourses.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Course Title
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Level</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Days Per Week
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Start Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        End Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingCourses.map((course) => (
                      <TableRow key={course.teacherCourseId}>
                        <TableCell>{course.courseTitle}</TableCell>
                        <TableCell>{course.courseLevel}</TableCell>
                        <TableCell>{course.courseType}</TableCell>
                        <TableCell>{course.daysPerWeek}</TableCell>
                        <TableCell>
                          {new Date(course.startDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(course.endDate).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>No pending courses available</Typography>
            )}
          </Collapse>
        </Box>

        {/* Approved Courses (Collapsible) */}
        <Box mt={4}>
          <Box
            onClick={() => setApprovedOpen(!approvedOpen)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: approvedOpen ? "#28a745" : "#6c757d", // Different color for approved courses toggle
              color: "#fff",
              padding: "8px 16px",
              borderRadius: "4px",
              transition: "background-color 0.3s",
              "&:hover": {
                backgroundColor: approvedOpen ? "#218838" : "#5a6268",
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              {approvedOpen ? "Hide Approved Courses" : "Show Approved Courses"}
            </Typography>
          </Box>
          <Collapse in={approvedOpen}>
            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              sx={{ textAlign: "center" }}
            >
              Approved Courses
            </Typography>
            {approvedCourses && approvedCourses.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Course Title
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Level</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Days Per Week
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Start Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        End Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedCourses.map((course) => (
                      <TableRow key={course.teacherCourseId}>
                        <TableCell>{course.courseTitle}</TableCell>
                        <TableCell>{course.courseLevel}</TableCell>
                        <TableCell>{course.courseType}</TableCell>
                        <TableCell>{course.daysPerWeek}</TableCell>
                        <TableCell>
                          {new Date(course.startDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(course.endDate).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>No approved courses available</Typography>
            )}
          </Collapse>
        </Box>
      </Container>
    </div>
  );
};

export default TeacherInformation;
